// 아바타 창 모델(레인 E): AvatarView → 화면 값. 순수(DOM 없음). 그리기는 avatarPanel.ts.
import {
  AVATAR_GRADES, AVATAR_SLOTS, CRAFT_CODE_KO, craftCheck, craftRecipes, AVATAR_SLOT_KO, AVATAR_STATS, AVATAR_STAT_CAP, AVATAR_STAT_KO,
  avatarDef, avatarSetDef, avatarSets, avatarStatText, avatarTradeInfo,
  type AvatarCraftCode, type AvatarDef, type AvatarGrade, type AvatarSlot, type AvatarStat, type AvatarStats, type AvatarView,
} from '../shared/avatar';

export interface AvatarCardView {
  readonly id: string; readonly name: string; readonly slot: AvatarSlot;
  readonly grade: AvatarGrade; readonly gradeName: string; readonly gradeColor: string;
  readonly icon: string; readonly tint: string;
  readonly statLines: readonly string[];
  readonly setName: string | null;
  readonly equipped: boolean; readonly tradable: boolean;
}
export interface AvatarSlotCell { readonly slot: AvatarSlot; readonly label: string; readonly card: AvatarCardView | null }
export interface AvatarTotalRow { readonly stat: AvatarStat; readonly label: string; readonly value: number; readonly text: string; readonly capped: boolean }
export interface AvatarSetRow { readonly id: string; readonly name: string; readonly count: number; readonly fx2: string; readonly fx3: string; readonly on2: boolean; readonly on3: boolean }
/** 제작 한 줄: 누를 수 있으면 can, 아니면 disabled 이유(label)가 버튼 글자 */
export interface CraftItemView {
  readonly id: string; readonly name: string; readonly grade: AvatarGrade; readonly gradeName: string; readonly gradeColor: string;
  readonly slotLabel: string; readonly tint: string;
  readonly shards: number; readonly gold: number; readonly costText: string;
  readonly code: AvatarCraftCode; readonly can: boolean; readonly label: string;
}
export interface CraftRegionView { readonly region: number; readonly shards: number; readonly items: readonly CraftItemView[] }
export interface CraftResultView { readonly ok: boolean; readonly text: string; readonly serial: number }
export interface AvatarPanelModel {
  readonly craft: readonly CraftRegionView[];
  readonly craftResult: CraftResultView | null;
  readonly slots: readonly AvatarSlotCell[];
  readonly owned: readonly AvatarCardView[];
  readonly totals: readonly AvatarTotalRow[];
  readonly sets: readonly AvatarSetRow[];
}

const GRADE_ORDER: Readonly<Record<AvatarGrade, number>> = { rare: 0, fine: 1, basic: 2 };

export function statLines(s: AvatarStats): string[] {
  const out: string[] = [];
  for (const k of AVATAR_STATS) { const v = s[k]; if (v) out.push(avatarStatText(k, v)); }
  return out;
}
export function hexColor(c: number): string { return '#' + (c & 0xffffff).toString(16).padStart(6, '0'); }

function cardOf(d: AvatarDef, equipped: boolean): AvatarCardView {
  const g = AVATAR_GRADES[d.grade];
  return {
    id: d.id, name: d.name, slot: d.slot, grade: d.grade, gradeName: g.name, gradeColor: g.color,
    icon: d.icon, tint: hexColor(d.color), statLines: statLines(d.stats),
    setName: avatarSetDef(d.set)?.name ?? null, equipped, tradable: avatarTradeInfo(d.id).tradable,
  };
}

export function goldText(n: number): string { return Math.round(n).toLocaleString('en-US') + 'G'; }

/** 조각을 가진 지역(없으면 빈 배열)의 제작 목록. 이미 가진 것·모자란 것도 이유와 함께 보인다 */
export function craftRegions(a: AvatarView, gold: number): CraftRegionView[] {
  const out: CraftRegionView[] = [];
  for (let region = 1; region <= 6; region++) {
    const have = a.shards[String(region)] ?? 0;
    // 조각이 있거나 이미 이 지역 제작품을 가진 지역만(조각 0개 지역이 줄줄이 보이지 않게)
    const recipes = craftRecipes(region);
    if (have <= 0) continue;
    const items = recipes.map((r): CraftItemView => {
      const d = avatarDef(r.id)!;
      const code = craftCheck(r, a.shards, gold, a.owned);
      const g = AVATAR_GRADES[d.grade];
      const label = code === 'ok' ? '만들기' : code === 'owned' ? '보유 중' : code === 'no_shards' ? `조각 ${have}/${r.shards}` : '골드 부족';
      return { id: r.id, name: d.name, grade: d.grade, gradeName: g.name, gradeColor: g.color, slotLabel: AVATAR_SLOT_KO[d.slot], tint: hexColor(d.color), shards: r.shards, gold: r.gold, costText: `조각 ${r.shards} · ${goldText(r.gold)}`, code, can: code === 'ok', label };
    });
    out.push({ region, shards: have, items });
  }
  return out;
}

export function craftResultOf(a: AvatarView): CraftResultView | null {
  const c = a.lastCraft;
  if (!c) return null;
  const name = avatarDef(c.id)?.name ?? '';
  return { ok: c.code === 'ok', text: (name ? name + ' · ' : '') + CRAFT_CODE_KO[c.code], serial: c.serial };
}

export function avatarPanelModel(a: AvatarView, gold = 0): AvatarPanelModel {
  const eqSet = new Set<string>();
  for (const id of a.equipped) if (id) eqSet.add(id);
  const slots = AVATAR_SLOTS.map((slot, i): AvatarSlotCell => {
    const d = avatarDef(a.equipped[i]);
    return { slot, label: AVATAR_SLOT_KO[slot], card: d ? cardOf(d, true) : null };
  });
  const owned: AvatarCardView[] = [];
  const seen = new Set<string>();
  for (const id of a.owned) {
    const d = avatarDef(id);
    if (!d || seen.has(d.id)) continue;
    seen.add(d.id);
    owned.push(cardOf(d, eqSet.has(d.id)));
  }
  const region = (id: string): number => avatarDef(id)?.source.region ?? 0;
  owned.sort((x, y) => GRADE_ORDER[x.grade] - GRADE_ORDER[y.grade] || region(x.id) - region(y.id));
  const totals: AvatarTotalRow[] = [];
  for (const k of AVATAR_STATS) {
    const v = a.totals[k];
    if (v > 0) totals.push({ stat: k, label: AVATAR_STAT_KO[k], value: v, text: avatarStatText(k, v), capped: v >= AVATAR_STAT_CAP[k] - 1e-9 });
  }
  const sets = avatarSets(a.equipped).map((p): AvatarSetRow => ({
    id: p.def.id, name: p.def.name, count: p.count,
    fx2: statLines(p.def.fx2).join(', '), fx3: statLines(p.def.fx3).join(', '), on2: p.on2, on3: p.on3,
  }));
  return { craft: craftRegions(a, gold), craftResult: craftResultOf(a), slots, owned, totals, sets };
}
